import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useActiveOnboarding, type OnboardingRun } from '../hooks/useActiveOnboarding.ts';

import { StatusBanner, type StatusKind } from './StatusBanner.tsx';

/**
 * Status banner pinned above the matrix views while a cloud onboarding
 * is in flight. Surfaces the current step + a friendly summary of
 * what's just happened (BFs / contexts / skills / clusters /
 * engineers written) so the user can see progress instead of staring
 * at an empty matrix.
 *
 * When the run terminates:
 *   - succeeded → 5-second success banner, then auto-dismisses. Matrix
 *     views are invalidated so they refetch the newly-populated catalog.
 *   - failed    → sticky banner with error_message + a dismiss button.
 *
 * Returns null (renders nothing) when no run is active or recent —
 * keeps the matrix's normal layout intact for the common case.
 */
export function OnboardingBanner() {
  const { data: run } = useActiveOnboarding();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const wasRunningRef = useRef(false);
  const queryClient = useQueryClient();

  // When a run transitions from running → succeeded, invalidate the
  // matrix view caches so the freshly-populated catalog appears
  // without a manual refresh.
  useEffect(() => {
    if (!run) return;
    const wasRunning = wasRunningRef.current;
    const isTerminal = run.status === 'succeeded' || run.status === 'failed';
    if (wasRunning && isTerminal) {
      void queryClient.invalidateQueries({ queryKey: ['view'] });
    }
    wasRunningRef.current = run.status === 'running' || run.status === 'queued';
  }, [run, queryClient]);

  if (!run) return null;
  if (dismissed === run.id) return null;

  // Hide succeeded banners ~30s after completion. Sticky-show failures.
  if (run.status === 'succeeded' && run.completed_at) {
    const completedMs = Date.parse(run.completed_at);
    if (!Number.isNaN(completedMs) && Date.now() - completedMs > 30_000) {
      return null;
    }
  }

  const { title, body, kind } = describe(run);
  const terminal = run.status === 'failed' || run.status === 'succeeded';
  return (
    <StatusBanner
      kind={kind}
      title={title}
      body={body}
      onDismiss={terminal ? () => setDismissed(run.id) : undefined}
    />
  );
}

interface Described {
  title: string;
  body: string | null;
  kind: StatusKind;
}

function describe(run: OnboardingRun): Described {
  if (run.status === 'failed') {
    return {
      title: 'Onboarding failed',
      body: run.error_message ?? null,
      kind: 'error',
    };
  }
  if (run.status === 'succeeded') {
    return {
      title: 'Onboarding complete',
      body: 'Catalog, ratings, and engineer roster are live in the matrix.',
      kind: 'success',
    };
  }
  // running / queued
  const step = run.current_step;
  const events = run.step_progress?.events ?? [];
  const stepLabel = STEP_LABELS[step ?? 'signals'] ?? 'Starting…';
  const stepIndex =
    step && STEP_INDEX[step] !== undefined ? `${STEP_INDEX[step]} of ${STEP_TOTAL}` : '';
  const summary = cumulativeSummary(events);
  return {
    title: stepIndex ? `Onboarding · step ${stepIndex} · ${stepLabel}` : `Onboarding · ${stepLabel}`,
    body: summary,
    kind: 'running',
  };
}

const STEP_LABELS: Record<string, string> = {
  signals: 'cloning the repo and wiping prior catalog',
  bfs: 'defining business functions',
  derivedContexts: 'defining contexts, skills, and file clusters',
  engineers: 'deriving engineer roster from commit history',
  observations: 'computing engineer ratings from commit history',
};
const STEP_INDEX: Record<string, number> = {
  signals: 1,
  bfs: 2,
  derivedContexts: 3,
  engineers: 4,
  observations: 5,
};
const STEP_TOTAL = 5;

/**
 * Walks the full event log and produces a cumulative summary of what
 * the orchestrator has CREATED so far — BFs by name, derivedContext count,
 * skill count, cluster count, engineer count. Replaces the prior
 * "last event" summary so the banner gives a running picture of the
 * run's output instead of jumping between transient messages.
 */
function cumulativeSummary(
  events: Array<{ kind: string; [k: string]: unknown }>,
): string | null {
  const bfDisplays: string[] = [];
  const bfFromDrafted: string[] = [];
  const compDisplays: string[] = [];
  const compFromDrafted: string[] = [];
  let skillCount = 0;
  let clusterCount = 0;
  let droppedClusterCount = 0;
  let engineers: number | null = null;
  let observations: number | null = null;
  for (const e of events) {
    switch (e.kind) {
      case 'bfs_drafted': {
        // LLM submitted its final BF set; the writes haven't fired
        // yet (those land as `bf_written` events milliseconds later)
        // but we can show the names now so the user sees the
        // outcome the moment the LLM converges, not 1-2s later.
        const drafted = e.drafted as Array<{ display?: string; id?: string }> | undefined;
        if (Array.isArray(drafted)) {
          bfFromDrafted.length = 0;
          for (const d of drafted) bfFromDrafted.push(String(d.display ?? d.id ?? '?'));
        }
        break;
      }
      case 'bf_written':
        bfDisplays.push(String(e.display ?? e.id ?? '?'));
        break;
      case 'derived_contexts_drafted': {
        const drafted = e.derivedContexts as Array<{ display?: string; id?: string }> | undefined;
        if (Array.isArray(drafted)) {
          compFromDrafted.length = 0;
          for (const d of drafted) compFromDrafted.push(String(d.display ?? d.id ?? '?'));
        }
        break;
      }
      case 'derived_context_written':
        compDisplays.push(String(e.display ?? e.id ?? '?'));
        break;
      case 'skill_written':
        skillCount += 1;
        break;
      case 'clusters_written':
        if (typeof e.count === 'number') clusterCount += e.count;
        break;
      case 'clusters_dropped':
        if (typeof e.count === 'number') droppedClusterCount += e.count;
        break;
      case 'engineers_derived':
        if (typeof e.output_engineers === 'number') engineers = e.output_engineers;
        break;
      case 'observations_derived':
        if (typeof e.observations_inserted === 'number') {
          observations = e.observations_inserted;
        }
        break;
    }
  }
  // Prefer written events (final state) over drafted (LLM intent
  // before writes) — but fall back to drafted while the writes are
  // mid-flight. Same for derivedContexts.
  const bfList = bfDisplays.length > 0 ? bfDisplays : bfFromDrafted;
  const compList = compDisplays.length > 0 ? compDisplays : compFromDrafted;
  const compCount = compList.length;
  const parts: string[] = [];
  if (bfList.length > 0) {
    parts.push(`${bfList.length} business function${bfList.length === 1 ? '' : 's'}: ${bfList.join(', ')}`);
  }
  if (compCount > 0) {
    const compNames = compList.length <= 4 ? `: ${compList.join(', ')}` : '';
    parts.push(`${compCount} context${compCount === 1 ? '' : 's'}${compNames}`);
  }
  if (skillCount > 0) parts.push(`${skillCount} skill${skillCount === 1 ? '' : 's'}`);
  if (clusterCount > 0) parts.push(`${clusterCount} cluster${clusterCount === 1 ? '' : 's'}`);
  if (droppedClusterCount > 0) {
    parts.push(`(${droppedClusterCount} cluster${droppedClusterCount === 1 ? '' : 's'} dropped — zero file matches)`);
  }
  if (engineers !== null) parts.push(`${engineers} engineer${engineers === 1 ? '' : 's'}`);
  if (observations !== null) parts.push(`${observations.toLocaleString()} observations`);
  if (parts.length === 0) return null;
  return parts.join(' · ');
}
